import React from 'react';
import Head from 'next/head';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Icon,
  SimpleGrid,
  Container,
  useColorMode,
  Badge,
  Divider,
  Card,
  CardBody,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { 
  FaAward, 
  FaRocket, 
  FaLeaf, 
  FaBullseye,
  FaCertificate,
  FaCalendarAlt,
  FaGlobe
} from 'react-icons/fa';

const MotionBox = motion(Box);
const MotionHeading = motion(Heading);
const MotionText = motion(Text);

const IntellectualProperty = () => {
  const { colorMode } = useColorMode();

  return (
    <>
      {/* SEO Meta Tags */}
      <Head>
        <title>Aptofy | Patented AI-Powered Hiring & Recruitment Platform</title>
        <meta name="description" content="Aptofy, developed by Unlimiteye Solutions, is a patented AI-powered recruitment platform. With Patent No. 569726 granted by the Patent Office, Govt. of India, Aptofy revolutionizes talent acquisition with real-time interview scoring, bias-free hiring, and reduced carbon emissions." />
        <meta name="keywords" content="Aptofy patent, Aptofy AI hiring, patented recruitment technology, Patent 569726, AI-powered hiring platform, bias-free recruitment, AI interviews, carbon footprint reduction, Unlimiteye Solutions" />
        <meta name="author" content="Unlimiteye Solutions Pvt. Ltd." />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.aptofy.com/intellectual-property" />
        <meta property="og:title" content="Aptofy | Patented AI-Powered Hiring & Recruitment Platform" />
        <meta property="og:description" content="Proudly patented! Aptofy is officially recognized with Patent No. 569726 for AI-powered recruitment, delivering secure, bias-free hiring and sustainable recruitment solutions." />
        <meta property="og:image" content="https://www.aptofy.com/images/patent-og.jpg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.aptofy.com/intellectual-property" />
        <meta name="twitter:title" content="Aptofy | Patented AI-Powered Hiring & Recruitment Platform" />
        <meta name="twitter:description" content="Aptofy, recently awarded Patent No. 569726, ensures AI-driven, bias-free recruitment with secure assessments and a focus on reducing carbon emissions." />
        <meta name="twitter:image" content="https://www.aptofy.com/images/patent-og.jpg" />
        
        {/* Extra SEO */}
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.aptofy.com/intellectual-property" />
      </Head>

      <Container maxW="1200px" py={12}>
        {/* Page Header */}
        <MotionBox
          textAlign="center"
          mb={12}
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <MotionHeading
            as="h1"
            size="2xl"
            mb={4}
            color={colorMode === 'dark' ? '#ffffff' : '#3A56D4'}
          >
            Our Intellectual Property
          </MotionHeading>
          <Text
            fontSize="lg"
            color={colorMode === 'dark' ? 'gray.300' : 'gray.600'}
            maxW="600px"
            mx="auto"
          >
            Innovation-driven solutions backed by patented technology
          </Text>
        </MotionBox>

        {/* Main Patent Card */}
        <MotionBox
          mb={12}
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Card
            bg={colorMode === 'dark' ? 'gray.800' : 'white'}
            border="1px solid"
            borderColor={colorMode === 'dark' ? 'gray.600' : 'gray.200'}
            boxShadow="xl"
            overflow="hidden"
          >
            <CardBody p={8}>
              <VStack spacing={6} textAlign="center">
                <Icon 
                  as={FaAward} 
                  boxSize={20} 
                  color="gold" 
                  filter="drop-shadow(0 0 15px rgba(255, 215, 0, 0.4))"
                />
                
                <Badge
                  colorScheme="green"
                  fontSize="md"
                  px={4}
                  py={2}
                  borderRadius="full"
                >
                  Patent Granted
                </Badge>
                
                <MotionHeading
                  as="h2"
                  size="lg"
                  color={colorMode === 'dark' ? '#ffffff' : 'blue.600'}
                  fontWeight="bold"
                  textAlign="center"
                >
                  System and Method for Conducting Interviews with Real-Time Scoring and Reducing Carbon Emission
                </MotionHeading>
                
                <Divider />
                
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} w="full">
                  <VStack>
                    <Icon as={FaCertificate} boxSize={6} color="blue.500" />
                    <Text fontWeight="semibold" fontSize="sm" color="gray.500">
                      Patent Number
                    </Text>
                    <Text
                      fontSize="lg"
                      fontWeight="bold"
                      color={colorMode === 'dark' ? 'blue.300' : 'blue.600'}
                    >
                      569726
                    </Text>
                  </VStack>
                  
                  <VStack>
                    <Icon as={FaGlobe} boxSize={6} color="green.500" />
                    <Text fontWeight="semibold" fontSize="sm" color="gray.500">
                      Granted By
                    </Text>
                    <Text
                      fontSize="md"
                      fontWeight="bold"
                      color={colorMode === 'dark' ? '#ffffff' : 'gray.800'}
                      textAlign="center"
                    >
                      The Patent Office<br />Govt. of India
                    </Text>
                  </VStack>
                  
                  <VStack>
                    <Icon as={FaCalendarAlt} boxSize={6} color="purple.500" />
                    <Text fontWeight="semibold" fontSize="sm" color="gray.500">
                      Status
                    </Text>
                    <Badge colorScheme="green" fontSize="md">
                      Active
                    </Badge>
                  </VStack>
                </SimpleGrid>
              </VStack>
            </CardBody>
          </Card>
        </MotionBox>

        {/* Key Innovation Areas */}
        <MotionBox
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Heading
            as="h3"
            size="lg"
            textAlign="center"
            mb={8}
            color={colorMode === 'dark' ? '#ffffff' : '#3A56D4'}
          >
            Key Innovation Areas
          </Heading>
          
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            <Card
              bg={colorMode === 'dark' ? 'gray.800' : 'white'}
              border="1px solid"
              borderColor={colorMode === 'dark' ? 'gray.600' : 'gray.200'}
              _hover={{ transform: 'translateY(-5px)' }}
              transition="all 0.3s"
            >
              <CardBody textAlign="center" p={6}>
                <Icon as={FaRocket} boxSize={12} color="blue.500" mb={4} />
                <Heading size="md" mb={3} color={colorMode === 'dark' ? '#ffffff' : 'inherit'}>
                  AI-Powered Technology
                </Heading>
                <Text color={colorMode === 'dark' ? 'gray.400' : 'gray.600'}>
                  Advanced artificial intelligence algorithms for real-time interview scoring and candidate assessment
                </Text>
              </CardBody>
            </Card>
            
            <Card
              bg={colorMode === 'dark' ? 'gray.800' : 'white'}
              border="1px solid"
              borderColor={colorMode === 'dark' ? 'gray.600' : 'gray.200'}
              _hover={{ transform: 'translateY(-5px)' }}
              transition="all 0.3s"
            >
              <CardBody textAlign="center" p={6}>
                <Icon as={FaLeaf} boxSize={12} color="green.500" mb={4} />
                <Heading size="md" mb={3} color={colorMode === 'dark' ? '#ffffff' : 'inherit'}>
                  Environmental Impact
                </Heading>
                <Text color={colorMode === 'dark' ? 'gray.400' : 'gray.600'}>
                  Revolutionary approach to reducing carbon emissions through digital-first recruitment processes
                </Text>
              </CardBody>
            </Card>
            
            <Card
              bg={colorMode === 'dark' ? 'gray.800' : 'white'}
              border="1px solid"
              borderColor={colorMode === 'dark' ? 'gray.600' : 'gray.200'}
              _hover={{ transform: 'translateY(-5px)' }}
              transition="all 0.3s"
            >
              <CardBody textAlign="center" p={6}>
                <Icon as={FaBullseye} boxSize={12} color="purple.500" mb={4} />
                <Heading size="md" mb={3} color={colorMode === 'dark' ? '#ffffff' : 'inherit'}>
                  Enhanced Efficiency
                </Heading>
                <Text color={colorMode === 'dark' ? 'gray.400' : 'gray.600'}>
                  Streamlined hiring processes that deliver faster, more accurate, and fairer recruitment outcomes
                </Text>
              </CardBody>
            </Card>
          </SimpleGrid>
        </MotionBox>

        {/* Patent Benefits */}
        <Box mt={16}>
          <Heading
            as="h3"
            size="lg"
            textAlign="center"
            mb={8}
            color={colorMode === 'dark' ? '#ffffff' : '#3A56D4'}
          >
            Why Our Patent Matters
          </Heading>
          
          <Text
            fontSize="lg"
            textAlign="center"
            mb={8}
            color={colorMode === 'dark' ? 'gray.300' : 'gray.600'}
            fontStyle="italic"
          >
            Aptofy: The Patented AI Solution Redefining Talent Acquisition
          </Text>
          
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <VStack align="start" spacing={4}>
              <HStack>
                <Icon as={FaCertificate} color="blue.500" />
                <Text fontWeight="semibold" color={colorMode === 'dark' ? '#ffffff' : 'inherit'}>
                  Proven Innovation
                </Text>
              </HStack>
              <Text color={colorMode === 'dark' ? 'gray.400' : 'gray.600'}>
                Our patented technology demonstrates our commitment to advancing the recruitment industry through innovative solutions.
              </Text>
            </VStack>
            
            <VStack align="start" spacing={4}>
              <HStack>
                <Icon as={FaRocket} color="purple.500" />
                <Text fontWeight="semibold" color={colorMode === 'dark' ? '#ffffff' : 'inherit'}>
                  Competitive Advantage
                </Text>
              </HStack>
              <Text color={colorMode === 'dark' ? 'gray.400' : 'gray.600'}>
                Exclusive rights to our breakthrough methodology ensure unique value proposition for our clients.
              </Text>
            </VStack>
            
            <VStack align="start" spacing={4}>
              <HStack>
                <Icon as={FaLeaf} color="green.500" />
                <Text fontWeight="semibold" color={colorMode === 'dark' ? '#ffffff' : 'inherit'}>
                  Sustainable Future
                </Text>
              </HStack>
              <Text color={colorMode === 'dark' ? 'gray.400' : 'gray.600'}>
                Contributing to environmental sustainability while revolutionizing the way companies hire talent.
              </Text>
            </VStack>
            
            <VStack align="start" spacing={4}>
              <HStack>
                <Icon as={FaBullseye} color="orange.500" />
                <Text fontWeight="semibold" color={colorMode === 'dark' ? '#ffffff' : 'inherit'}>
                  Quality Assurance
                </Text>
              </HStack>
              <Text color={colorMode === 'dark' ? 'gray.400' : 'gray.600'}>
                Patent protection ensures the integrity and quality of our innovative recruitment solutions.
              </Text>
            </VStack>
          </SimpleGrid>
        </Box>
      </Container>
    </>
  );
};

export default IntellectualProperty;